/**
 * New page → Build it myself (designer's notes, 2026-10-25): the dialog's
 * rules, kept apart so they can be tested. The server checks the same
 * (tinycms/services/newPage.js) and has the last word.
 */

export type PageStart = 'text' | 'contact' | 'landing' | 'blank';

export const PAGE_STARTS: Array<{ id: PageStart; label: string; sub: string }> = [
  { id: 'text', label: 'Text page', sub: 'A heading and a few paragraphs, like About' },
  { id: 'contact', label: 'Contact page', sub: 'Text plus a contact form' },
  { id: 'landing', label: 'Landing page', sub: 'A big headline, highlights and one button' },
  { id: 'blank', label: 'Blank', sub: 'Just a heading to build from' },
];

/** The address the title gives, until the person types their own. */
export function pathFromTitle(title: string): string {
  return title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

/** What the person types in Address, kept to what an address can hold. */
export function cleanTypedPath(typed: string): string {
  return typed
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9/-]/g, '');
}

/** One part, or a group and a part: "about", "docs/setup". */
export const PATH_RULE = /^[a-z0-9]+(-[a-z0-9]+)*(\/[a-z0-9]+(-[a-z0-9]+)*)?$/;

export const PATH_HELP = 'Filled in from the title. Lowercase letters, numbers and dashes.';

/** What's wrong with the dialog, if anything, once Create was tried. */
export function newPageProblems(
  title: string,
  path: string,
  takenPaths: Iterable<string>
): { title: string | null; path: string | null } {
  const taken = new Set(Array.from(takenPaths, (p) => p.replace(/^\/+/, '')));
  return {
    title: title.trim() ? null : 'Add a title',
    path: !PATH_RULE.test(path)
      ? 'Use lowercase letters, numbers and dashes, with an optional / between parts'
      : taken.has(path)
        ? `There’s already a page at /${path}. Pick another address.`
        : null,
  };
}

/**
 * Pages the person made with New page in this visit: their draft says "Your
 * draft page" (nothing on the server records who made a page).
 */
export const madeHere = new Set<string>();
